import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import Select from 'react-select'

import {
	StateOptions,
	TApi,
	TicketOriginOptions,
	TicketStatusOptions,
	TicketTypeOptions,
	TTicketOrigin
} from '../../../../shared/const'
import { stateFullLabel, stateLabel, ticketStatusLabel, ticketTypeLabel } from '../../../../shared/helpers'
import { formatDate as formatDateInVienna, formatVienna } from '../../../../shared/datetime'
import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import { readTicketListParams } from '../../../../shared/ticketListLink'

import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import useAttentionPulse, { PULS_KLASSE } from '../../../../shared/useAttentionPulse'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import ListToolbar from '../../../../UI/ListToolbar'
import RoleChip from '../../../../UI/RoleChip'

import TicketDetail from './TicketDetail'
import TicketFormModal from './TicketFormModal'
import ProjectsModal from './ProjectsModal'
import AssigneeBars from './AssigneeBars'
import ActiveFilters from './ActiveFilters'
import OriginBar from './OriginBar'
import RequestStats from './RequestStats'
import StatusDonut from './StatusDonut'
import StatusFunnel from '../../../../UI/StatusFunnel'

import { funnelStufen } from './statusHints'
import styles from './style.module.scss'

/*
 * Die Spalten der Listenansicht. `sortField` ist der Wert, den der Server als
 * Sortierschlüssel kennt (siehe tickets/schema.js); fehlt er, ist die Spalte
 * nicht sortierbar. `sortDefault` legt fest, in welche Richtung ein erster
 * Klick sortiert — Zahlen und Daten absteigend (Größtes/Neuestes zuerst), Text
 * aufsteigend (A–Z).
 */
const COLUMNS = [
	{ key: 'id', label: 'Nr.', sortField: 'id', sortDefault: 'desc', className: 'colId' },
	{ key: 'title', label: 'Titel', sortField: 'title', sortDefault: 'asc' },
	{ key: 'type', label: 'Art', sortField: 'type', sortDefault: 'asc' },
	{ key: 'status', label: 'Status', sortField: 'status', sortDefault: 'asc' },
	{
		key: 'score',
		label: 'Score',
		sortField: 'score',
		sortDefault: 'desc',
		className: 'colScore',
		title: 'Dringlichkeit × Wichtigkeit'
	},
	{ key: 'project', label: 'Projekt', sortField: 'project', sortDefault: 'asc' },
	/*
	 * Zwei Zählspalten, beide schmal — ausgeschrieben kosteten „Anhänge" und
	 * „Kommentare" mehr Breite als die Zahlen darunter je brauchen. Der Kopf
	 * ist deshalb ein Symbol; `icon` sagt welches, `label` bleibt als Wort für
	 * den Tooltip, die Sortierliste und die Vorlesehilfe.
	 */
	{
		key: 'screenshots',
		label: 'Anhänge',
		icon: 'clip',
		sortField: 'screenshotCount',
		sortDefault: 'desc',
		className: 'colShots'
	},
	{
		key: 'comments',
		label: 'Kommentare',
		icon: 'bubble',
		sortField: 'commentCount',
		sortDefault: 'desc',
		className: 'colShots'
	},
	/*
	 * Zuletzt geändert statt erfasst: Wann eine Anforderung angelegt wurde,
	 * steht seit 24.08.2026 unter dem Namen in „Erfasst von". Was eine Liste
	 * von 500 Zeilen sortierbar machen soll, ist die andere Frage — woran
	 * zuletzt gearbeitet wurde.
	 */
	{ key: 'updatedAt', label: 'Änderung', sortField: 'updatedAt', sortDefault: 'desc' },
	/*
	 * Die beiden Personenspalten stehen seit 06.10.2026 am Ende — „Erfasst von“
	 * vorletzt, „Zugewiesen an“ zuletzt: Sie sind die breitesten Zellen der
	 * Zeile und rücken Status, Score und Projekt sonst vom Titel weg.
	 */
	{ key: 'createdBy', label: 'Erfasst von', sortField: 'createdBy', sortDefault: 'asc' },
	{ key: 'assignedTo', label: 'Zugewiesen an', sortField: 'assignedTo', sortDefault: 'asc' }
]

// Ausformulierte Namen für die Sortier-Auswahlliste (Kachelansicht) und für den
// Eintrag, der die aktuell per Spaltenklick gewählte Sortierung dort spiegelt.
const SORT_LABELS = {
	id: 'Nummer',
	title: 'Titel',
	type: 'Art',
	status: 'Status',
	score: 'Score',
	project: 'Projekt',
	assignedTo: 'Zugewiesen an',
	createdBy: 'Erfasst von',
	screenshotCount: 'Anhänge',
	commentCount: 'Kommentare',
	createdAt: 'Erfasst',
	updatedAt: 'Zuletzt geändert'
}

const sortOptionLabel = value => {
	const [field, direction] = value.split(':')
	return `${SORT_LABELS[field] || field} ${direction === 'asc' ? '▲' : '▼'}`
}

// Kuratierte Voreinstellungen für die Auswahlliste. Die Spaltenklicks decken
// alles ab; die Liste bleibt als schneller Zugriff und für die Kachelansicht,
// die keine Spaltenköpfe hat.
const SORT_OPTIONS = [
	{ value: 'score:desc', label: 'Score, höchster zuerst' },
	{ value: 'score:asc', label: 'Score, niedrigster zuerst' },
	{ value: 'id:desc', label: 'Nummer, neueste zuerst' },
	{ value: 'id:asc', label: 'Nummer, älteste zuerst' },
	{ value: 'updatedAt:desc', label: 'Zuletzt geändert' },
	{ value: 'status:asc', label: 'Status' },
	{ value: 'title:asc', label: 'Titel A–Z' }
]

// Auf der Kachel steht nur der Anfang, der Rest kommt per "Mehr lesen".
// Sonst werden die Kacheln unterschiedlich hoch und das Raster zerfaellt.
const KACHEL_ZEICHEN = 160

// Merker des Auswertungsblocks im Browser — wie bei den übrigen zuklappbaren
// Bereichen der Anwendung.
const AUSWERTUNG_KEY = 'junior.tickets.auswertung'

const EMPTY_FILTERS = {
	status: [],
	type: [],
	projectId: [],
	assignedTo: [],
	archived: [],
	state: [],
	herkunft: []
}

/*
 * Archiviert wird ein Jahr nach dem Abschluss, und dann ist der Vorgang aus der
 * Arbeit heraus (Anfragen-Konzept, Abschnitt 11). Der Filter ist deshalb ein
 * Umschalter und keine Mehrfachauswahl: Wer ihn setzt, sucht **im Archiv** —
 * eine Liste, die beides mischt, beantwortet keine der beiden Fragen.
 */
const ARCHIV_OPTIONEN = [{ value: 'archiv', label: 'Nur Archiv' }]

/*
 * Das zuständige Bundesland einer Anfrage. Die „0" steht wie bei Projekt und
 * Zuweisung für „ohne" — hier also für bundesweit, die Gruppe ohne Bundesland.
 */
/*
 * Wer hat gemeldet? Bei einer intern erfassten Anforderung steht dort die
 * erfassende Verwaltungsperson, bei einer Anfrage über den Hilfe-Knopf die
 * meldende — und die Rolle als Kürzel davor, weil es einen Unterschied macht,
 * ob eine Schülerin oder eine Landesadministration schreibt.
 */
const herkunft = ticket => {
	if (ticket.origin !== 'ENDUSER') {
		return { extern: false, name: ticket.createdBy?.name || '—', rolle: ticket.createdBy?.role }
	}

	return {
		extern: true,
		/*
		 * Die Rolle **beim Melden** geht vor der Rolle am Konto: Der `context`
		 * hält fest, aus welcher Ecke die Meldung kam, und überlebt auch ein
		 * inzwischen gelöschtes Konto (dann steht als Name der Gedankenstrich,
		 * die Rolle aber weiter da).
		 */
		rolle: ticket.context?.role || ticket.reporter?.role,
		name: ticket.reporter?.name || '—',
		land: ticket.responsibleState ? stateLabel(ticket.responsibleState) : 'bundesweit'
	}
}

/*
 * Die Wörter der Herkunftsleiste. `TicketOriginOptions` kennt nur die Rollen;
 * die beiden Sammelwerte stehen dort als eigene Schaltflächen und fehlen
 * deshalb in der Liste.
 */
const HERKUNFT_WOERTER = {
	[TTicketOrigin.INTERNAL]: 'Intern erfasst',
	[TTicketOrigin.ENDUSER]: 'Von Nutzer:innen',
	...TicketOriginOptions.reduce((woerter, option) => {
		woerter[option.value] = option.label
		return woerter
	}, {})
}

const BUNDESLAND_OPTIONEN = [
	{ value: '0', label: 'Bundesweit' },
	...[...StateOptions].sort((a, b) => a.label.localeCompare(b.label, 'de'))
]

/*
 * Die Sichten auf dieselbe Liste. Sie sind keine Filter der Leiste, sondern
 * die Frage davor: Geht mich das etwas an? Deshalb Reiter und keine Chips —
 * und deshalb bleiben sie beim „Filter zurücksetzen" stehen.
 *
 * „Alle Anforderungen" steht vorne und ist die Startsicht. Wer den Bereich zum
 * ersten Mal öffnet, hat in der Regel noch nichts zugewiesen bekommen; mit „an
 * mich" als Start stünde dort eine leere Liste, und der Eindruck wäre, die
 * Anforderungen seien weg.
 *
 * An jedem Reiter steht, wie viel dort noch offen ist — weder abgeschlossen
 * noch verworfen. Nicht die Gesamtzahl: Dass es achtzig Anforderungen gibt, von
 * denen siebenundfünfzig abgeschlossen sind, hilft niemandem beim Entscheiden,
 * was als Nächstes dran ist.
 *
 * Seit 22.08.2026 fragen die drei hinteren Sichten nach der **Zuweisung**, nicht
 * mehr nach der Art: „Aufgabe" war nie eine Art. Ein mir zugewiesener Fehler
 * stand deshalb in keiner von ihnen — jetzt steht er in „Mir zugewiesen".
 * Damit gilt in jeder Sicht dieselbe Statuskette, und der Art-Filter bleibt
 * überall stehen.
 */
const SCOPES = [
	{ key: 'all', label: 'Alle' },
	{ key: 'assignedToMe', label: 'Mir zugewiesen' },
	{ key: 'delegated', label: 'Von mir vergeben' },
	{ key: 'assignedAny', label: 'Alle zugewiesenen' },
	/*
	 * Seit 22.08.2026 koennen Endnutzer:innen ueber den Hilfe-Knopf melden, und
	 * zustaendig ist dann ein **Bundesland**, keine Person. Diese Sicht zeigt,
	 * was dem eigenen Bundesland zugeordnet wurde — nur fuer Landesadmins:
	 * Die Bundesebene hat kein eigenes Bundesland und sieht ohnehin alles.
	 */
	{ key: 'myState', label: 'An mein Bundesland', nurLandesadmin: true }
]

// Die drei Zuweisungs-Sichten. Was sie bedeuten, weiß der Server (services/
// tickets/scopes.js) — hier steht nur, welche es sind.
const ZUWEISUNGS_SICHTEN = ['assignedToMe', 'delegated', 'assignedAny']

/*
 * Was aus der Adresszeile übernommen wird — die Kacheln des Dashboards führen
 * mit `?status=…` und `?scope=…` hierher (shared/ticketListLink). Alles andere
 * wird verworfen, damit ein Tippfehler keine unerklärlich leere Liste ergibt.
 */
const ERLAUBTE_PARAMETER = {
	status: TicketStatusOptions.map(option => option.value),
	scopes: SCOPES.map(entry => entry.key),
	// Der Sammelwert steht nicht in den Optionen — die Leiste zeigt ihn als
	// eigene Schaltfläche, und die Kachel „Von Nutzer:innen" verweist darauf.
	herkunft: [TTicketOrigin.INTERNAL, TTicketOrigin.ENDUSER, ...TicketOriginOptions.map(option => option.value)]
}

/*
 * „An mein Bundesland" ohne eigenes Bundesland gibt es nicht: Der Reiter fehlt
 * dann (siehe `sichten`), und eine aktive Sicht ohne sichtbaren Reiter wäre ein
 * Filter, den niemand mehr abschalten kann.
 */
const sichtAusAdresse = (scope, eigenesLand) => {
	if (!scope) return 'all'
	if (scope === 'myState' && !eigenesLand) return 'all'
	return scope
}

// Eine leere Liste soll sagen, warum sie leer ist — sonst sucht man den Fehler
// bei den Daten statt beim gewählten Reiter.
const leerText = scope => {
	if (scope === 'assignedToMe') return 'Ihnen ist derzeit nichts zugewiesen.'
	if (scope === 'delegated') return 'Sie haben derzeit nichts an jemand anderen vergeben.'
	if (scope === 'assignedAny') return 'Derzeit ist nichts zugewiesen.'
	return 'Noch keine Anforderungen erfasst.'
}

// Tag und Monat zweistellig: „24.08.2026" statt „24.8.2026". In einer Spalte
// mit 90 Zeilen stehen die Punkte sonst nicht untereinander.
const formatDatum = value =>
	formatDateInVienna(value, {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})

const formatUhrzeit = value => formatVienna(value, 'HH:mm')

/**
 * Ein Zeitpunkt in zwei Zeilen: Datum oben, Uhrzeit darunter.
 *
 * Untereinander und nicht nebeneinander, weil die Spalten sonst breit werden
 * und die Datumsangaben in einer langen Liste nicht mehr untereinander stehen
 * — dann sucht das Auge jedes Datum einzeln. Die Uhrzeit gehört dazu, weil an
 * einer Anforderung mehrmals am Tag etwas geschieht.
 */
const Zeitstempel = ({ wert }) =>
	wert ? (
		<span className={styles.zeitstempel}>
			<span>{formatDatum(wert)}</span>
			<span className={styles.uhrzeit}>{formatUhrzeit(wert)}</span>
		</span>
	) : null

Zeitstempel.propTypes = {
	wert: PropTypes.string
}

/**
 * Name mit dem zugehörigen Zeitpunkt darunter.
 *
 * Für „Zugewiesen an" und „Erfasst von": Der Name allein sagt nicht, ob die
 * Zuweisung von heute oder von vor drei Wochen ist. Fehlt der Name, steht dort
 * der Gedankenstrich und kein Datum — ein Zeitpunkt ohne Person wäre eine
 * Angabe über niemanden.
 */
const NameMitDatum = ({ name, rolle, datum }) =>
	name ? (
		<span className={styles.stapel}>
			{/*
			 * Name und Rollenschild in einer umbrechenden Zeile: Passt beides
			 * nicht nebeneinander, rutscht das Schild unter den Namen — statt
			 * die Spalte breiter zu machen und die übrigen aus dem Bild zu
			 * schieben.
			 */}
			<span className={styles.nameZeile}>
				{name}
				<RoleChip role={rolle} />
			</span>
			<span className={styles.stapelDatum}>
				<Zeitstempel wert={datum} />
			</span>
		</span>
	) : (
		<>—</>
	)

NameMitDatum.propTypes = {
	name: PropTypes.node,
	// Rolle des Kontos — steht als Schild neben dem Namen. Fehlt sie, fehlt das
	// Schild (gelöschtes Konto).
	rolle: PropTypes.string,
	datum: PropTypes.string
}

/*
 * Die Symbole der beiden Zählspalten. Strich-Zeichnungen in `currentColor`,
 * damit sie die Farbe des Spaltenkopfs annehmen — auch die dunklere der gerade
 * sortierten Spalte.
 */
const SPALTEN_SYMBOLE = {
	// Büroklammer für die Anhänge.
	clip: (
		<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
	),
	// Sprechblase für die Kommentare.
	bubble: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
}

/**
 * Spaltenkopf, der nur aus einem Symbol besteht.
 *
 * Der Name geht dabei nicht verloren: Er steht im Tooltip (`title` am `th`)
 * und als `aria-label` für die Vorlesehilfe.
 */
const SpaltenSymbol = ({ name }) => (
	<svg
		className={styles.kopfSymbol}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		{SPALTEN_SYMBOLE[name]}
	</svg>
)

SpaltenSymbol.propTypes = {
	name: PropTypes.oneOf(Object.keys(SPALTEN_SYMBOLE)).isRequired
}

/*
 * Der Score reicht von 1 bis 9. Ab 6 wird es dringend UND wichtig - das soll
 * in einer langen Liste ins Auge fallen, ohne dass man erst rechnen muss.
 */
const scoreClass = score => {
	if (score >= 6) return `${styles.score} ${styles.scoreHigh}`
	if (score >= 3) return `${styles.score} ${styles.scoreMid}`
	return `${styles.score} ${styles.scoreLow}`
}

/**
 * Anforderungsverwaltung.
 *
 * Die Liste holt der Server — Suche, Filter und Sortierung laufen dort, nicht
 * hier. Bei einer im Laufe der Zeit wachsenden Liste wäre das Filtern im
 * Browser sonst irgendwann die Bremse.
 */
export default function Tickets() {
	const [rows, setRows] = useState(null)
	const [count, setCount] = useState(null)
	const [statusCounts, setStatusCounts] = useState({})
	const [assigneeCounts, setAssigneeCounts] = useState({})
	const [originCounts, setOriginCounts] = useState({})
	// Ob es überhaupt Meldungen von außen gibt — ungefiltert, siehe OriginBar.
	const [enduserTotal, setEnduserTotal] = useState(0)
	const [projects, setProjects] = useState([])
	const [assignees, setAssignees] = useState([])
	const [scopeCounts, setScopeCounts] = useState(null)
	/*
	 * Die Auswertung je Bundesland haengt an dieser Zahl. Sie holt ihre Werte
	 * in einem eigenen Aufruf und wuerde sonst den Stand vom Oeffnen der Seite
	 * behalten - eine gerade abgeschlossene Anfrage stuende dort noch als
	 * "offen".
	 */
	const [statsKey, setStatsKey] = useState(0)

	/*
	 * Der Einstieg von außen: Eine Kachel auf dem Dashboard nennt eine Zahl und
	 * führt hierher — die Liste muss dann genau die Zeilen zeigen, die auf der
	 * Kachel standen.
	 *
	 * Nur beim Öffnen gelesen und danach nie wieder: Ab da gehört der Filter der
	 * Person vor dem Bildschirm. Die Liste schreibt ihren Stand auch nicht in
	 * die Adresszeile zurück (Begründung in shared/ticketListLink).
	 */
	const location = useLocation()
	const history = useHistory()
	// „An mein Bundesland" braucht ein eigenes Bundesland — steht hier oben, weil
	// schon die Startsicht danach fragt.
	const eigenesLand = useSelector(state => state.user.profile?.state)
	const [vonAdresse] = useState(() => readTicketListParams(location.search, ERLAUBTE_PARAMETER))

	const [search, setSearch] = useState('')
	const [filters, setFilters] = useState(() => ({
		...EMPTY_FILTERS,
		status: vonAdresse.status,
		herkunft: vonAdresse.herkunft
	}))
	const [scope, setScope] = useState(() => sichtAusAdresse(vonAdresse.scope, eigenesLand))
	const [sort, setSort] = useState('score:desc')
	const [view, setView] = useState('list')

	const scopeRef = useRef(null)
	useScrollTopOnTabChange(scope, scopeRef)

	const [openId, setOpenId] = useState(null)
	// Die Blätter-Reihenfolge des Details wird beim Öffnen eingefroren. Käme sie
	// live aus `rows`, würde eine Änderung im Detail (Status, Projekt), durch die
	// das Ticket aus dem aktiven Filter fällt, das Blättern abbrechen — das
	// Ticket stünde dann nicht mehr in der Liste.
	const [detailSiblings, setDetailSiblings] = useState([])
	const [editing, setEditing] = useState(null)
	const [showProjects, setShowProjects] = useState(false)

	const [expanded, setExpanded] = useState({})

	/*
	 * Der Auswertungsblock (Herkunft, Verteilung, Anfragen je Bundesland) ist
	 * zugeklappt voreingestellt: Er steht über einer Liste, die man eigentlich
	 * sehen will, und beantwortet Fragen, die man nicht jeden Tag stellt. Wer
	 * ihn aufklappt, findet ihn beim nächsten Mal offen vor.
	 */
	const [auswertungOffen, setAuswertungOffen] = useState(() => window.localStorage.getItem(AUSWERTUNG_KEY) === 'auf')

	const klappeAuswertung = () =>
		setAuswertungOffen(vorher => {
			try {
				window.localStorage.setItem(AUSWERTUNG_KEY, vorher ? 'zu' : 'auf')
			} catch (error) {
				// Ohne Speicher gilt die Wahl nur für diesen Besuch.
			}
			return !vorher
		})

	// Ohne eigenes Bundesland zeigte der Reiter dauerhaft eine leere Liste —
	// besser gar nicht erst da.
	const sichten = useMemo(() => SCOPES.filter(entry => !entry.nurLandesadmin || Boolean(eigenesLand)), [eigenesLand])

	/*
	 * Der Reiter des eigenen Bundeslandes leuchtet beim Anzeigen kurz auf —
	 * zwei Schübe, dann Ruhe (`shared/useAttentionPulse`, wie bei den aktiven
	 * Filtern). Nur, wenn dort wirklich etwas offen ist: Eine leuchtende Null
	 * ruft nach einer Handlung, die es nicht gibt.
	 */
	const landPuls = useAttentionPulse(Boolean(eigenesLand) && Boolean(scopeCounts?.myState))

	// Mehrfachauswahl für Sammelaktionen (Projekt zuweisen, Status setzen).
	const [selected, setSelected] = useState([])

	const loadProjects = useCallback(() => {
		http.get(TApi.ADMIN_TICKET_PROJECTS)
			.then(({ data }) => setProjects(data))
			.catch(() => setProjects([]))
	}, [])

	// Wem sich etwas zuweisen laesst: die Administration. Einmal geholt und an
	// beide Fenster weitergereicht - eine Auswahlliste je Fenster waere derselbe
	// Aufruf zweimal.
	const loadAssignees = useCallback(() => {
		http.get(TApi.ADMIN_TICKET_ASSIGNEES)
			.then(({ data }) => setAssignees(data))
			.catch(() => setAssignees([]))
	}, [])

	// Die Zahlen an den Reitern. Kommen sie nicht, bleiben die Reiter ohne Zahl
	// statt mit einer falschen - `null` heisst "unbekannt", nicht "keine".
	const loadCounts = useCallback(() => {
		http.get(TApi.ADMIN_TICKET_COUNTS, { disableToast: true })
			.then(({ data }) => setScopeCounts(data))
			.catch(() => setScopeCounts(null))
	}, [])

	const load = useCallback(() => {
		const [order, direction] = sort.split(':')

		// `disableToast`: Der eigene Satz benennt, was nicht geladen werden konnte.
		http.get(TApi.ADMIN_TICKETS, {
			params: {
				search,
				// Der Server erwartet einen Schalter, die Leiste liefert eine
				// Auswahl — hier wird aus „eine Option gewählt" ein `true`.
				filter: JSON.stringify({ ...filters, archived: filters.archived.length > 0 }),
				scope,
				order,
				direction,
				limit: 200
			},
			disableToast: true
		})
			.then(({ data }) => {
				setRows(data.rows)
				setCount(data.count)
				setStatusCounts(data.statusCounts || {})
				setAssigneeCounts(data.assigneeCounts || {})
				setOriginCounts(data.originCounts || {})
				setEnduserTotal(data.enduserTotal || 0)
				// Was aus der Liste fällt (Suche, Filter), fliegt auch aus der
				// Auswahl — sonst träfe eine Sammelaktion unsichtbare Zeilen.
				setSelected(prev => prev.filter(id => data.rows.some(row => row.id === id)))
			})
			.catch(error => {
				notifyError(error, 'Die Anforderungen konnten nicht geladen werden.')
				setRows([])
				setCount(0)
				setStatusCounts({})
				setAssigneeCounts({})
				setOriginCounts({})
				setEnduserTotal(0)
			})
	}, [search, filters, scope, sort])

	useEffect(() => {
		loadProjects()
		loadAssignees()
		loadCounts()
	}, [loadProjects, loadAssignees, loadCounts])

	// Verzögert nachladen: Ohne das schickt jede getippte Taste eine Abfrage.
	useEffect(() => {
		const timer = setTimeout(load, 250)
		return () => clearTimeout(timer)
	}, [load])

	// Status wandert in den Funnel, Projekt in eine Auswahl mit Suche - beide
	// sind darum keine Chip-Filter mehr. In der Leiste bleibt nur die Art — und
	// zwar in jeder Sicht: Seit die Reiter nach der Zuweisung fragen, ist die
	// Art dort nicht mehr vorbestimmt.
	const filterConfig = useMemo(
		() => [
			{ key: 'type', label: 'Art', options: TicketTypeOptions },
			{ key: 'state', label: 'Bundesland', options: BUNDESLAND_OPTIONEN },
			{ key: 'archived', label: 'Archiv', options: ARCHIV_OPTIONEN }
		],
		[]
	)

	// 0 ist keine echte Kennung, sondern "ohne Projekt". Der Server kennt sie.
	const projectOptions = useMemo(
		() => [
			{ value: 0, label: 'Ohne Projekt' },
			...projects.map(project => ({ value: project.id, label: project.title }))
		],
		[projects]
	)

	const [sortField, sortDir] = sort.split(':')

	// Eine Kette, gleich welche Sicht und welche Art (22.08.2026). Funnel, Ring
	// und Sammelaktion zeigen dieselben sieben Stufen mit denselben Wörtern.
	const inZuweisungsSicht = ZUWEISUNGS_SICHTEN.includes(scope)

	/*
	 * Ob im Auswertungsblock überhaupt etwas steht.
	 *
	 * Die drei Teile blenden sich je für sich aus, und jeder tut das zu Recht:
	 * eine Herkunftsleiste mit vier Nullen und eine Bundesland-Tabelle mit neun
	 * Nullzeilen sagen nichts. Treffen die Bedingungen zusammen — der Fall seit
	 * dem Go-Live, solange niemand über den Hilfe-Knopf gemeldet hat —, klappt
	 * ein Block auf, in dem nichts steht. Das ist der Zustand, den dieser Wert
	 * erkennt; der Satz dazu steht unten im Block.
	 *
	 * Die Bedingungen sind bewusst dieselben wie in den Teilen selbst und nicht
	 * knapper geschrieben: Wer dort eine ändert, findet sie hier wieder.
	 */
	const zeigtZuweisung =
		inZuweisungsSicht &&
		scope !== 'assignedToMe' &&
		Object.values(assigneeCounts || {}).some(anzahl => Number(anzahl) > 0)
	const zeigtHerkunft = Boolean(enduserTotal) || Boolean(filters.herkunft.length)
	const auswertungLeer = !zeigtZuweisung && !zeigtHerkunft
	const { options: statusOptions, hints: statusHints } = funnelStufen()

	// Klick auf einen Spaltenkopf: dieselbe Spalte kehrt die Richtung um, eine
	// andere startet mit ihrer Vorzugsrichtung.
	const sortBy = column => {
		if (!column.sortField) return
		setSort(prev => {
			const [field, direction] = prev.split(':')
			if (field === column.sortField) {
				return `${field}:${direction === 'asc' ? 'desc' : 'asc'}`
			}
			return `${column.sortField}:${column.sortDefault}`
		})
	}

	// Die Auswahlliste soll die aktuelle Sortierung zeigen, auch wenn sie per
	// Spaltenklick auf einen Wert außerhalb der Voreinstellungen gesetzt wurde.
	const sortOptions = useMemo(() => {
		if (SORT_OPTIONS.some(option => option.value === sort)) return SORT_OPTIONS
		return [{ value: sort, label: sortOptionLabel(sort) }, ...SORT_OPTIONS]
	}, [sort])

	// Mehrfachauswahl: der Server-Vertrag ist mehrwertig (projectId als Liste,
	// 0 = „ohne Projekt"). Die Auswahl spiegelt alle gewählten Projekte.
	const selectedProjects = projectOptions.filter(option => filters.projectId.includes(option.value))

	/*
	 * Reiterwechsel. Der Status bleibt stehen — mit der einen Kette kennt jede
	 * Sicht jeden Status. Bis 22.08.2026 musste hier umgeschlüsselt werden, sonst
	 * blieb ein Status unsichtbar aktiv und die Liste ohne erkennbaren Grund leer.
	 */
	const setzeScope = next => {
		setScope(next)
		// Die Personen-Auswahl gilt nur für die Verteilung, die man gerade sieht
		// — in der nächsten Sicht wäre sie eine unsichtbare Einschränkung.
		setFilters(prev => ({ ...prev, assignedTo: [] }))
	}

	const reset = () => {
		setSearch('')
		setFilters(EMPTY_FILTERS)
	}

	/*
	 * Alles, was die Liste gerade einschränkt — aus allen sechs Quellen in
	 * einer Aufzählung. Die Reihenfolge ist die der Oberfläche von oben nach
	 * unten, damit ein Schild dort zu suchen ist, wo es gesetzt wurde.
	 *
	 * Die Sicht der Reiterleiste steht bewusst **nicht** dabei: Sie ist die
	 * Frage davor („geht mich das etwas an?") und bleibt auch beim
	 * Zurücksetzen stehen.
	 */
	const aktiveFilter = useMemo(() => {
		const projektNamen = projects.reduce((namen, projekt) => {
			namen[projekt.id] = projekt.shortTitle || projekt.title
			return namen
		}, {})
		const personenNamen = assignees.reduce((namen, person) => {
			namen[person.id] = person.name
			return namen
		}, {})

		const eintraege = []
		const nimm = (feld, gruppe, benenne) =>
			(filters[feld] || []).forEach(wert =>
				eintraege.push({
					id: `${feld}:${wert}`,
					gruppe,
					label: benenne(wert),
					feld,
					wert
				})
			)

		nimm('status', 'Status', ticketStatusLabel)
		nimm('assignedTo', 'Zugewiesen an', wert =>
			// Die 0 ist keine Kennung, sondern „ohne" — dieselbe Übereinkunft
			// wie im Projekt- und im Bundeslandfilter.
			Number(wert) === 0 ? 'Niemandem zugewiesen' : personenNamen[wert] || `Konto ${wert}`
		)
		nimm('herkunft', 'Herkunft', wert => HERKUNFT_WOERTER[wert] || wert)
		nimm('state', 'Bundesland', wert => (wert === '0' ? 'Bundesweit' : stateLabel(wert)))
		nimm('type', 'Art', ticketTypeLabel)
		nimm('projectId', 'Projekt', wert =>
			Number(wert) === 0 ? 'Ohne Projekt' : projektNamen[wert] || `Projekt ${wert}`
		)
		nimm('archived', 'Archiv', () => 'Nur Archiv')

		if (search.trim()) {
			eintraege.push({ id: 'search', gruppe: 'Suche', label: search.trim(), feld: 'search' })
		}

		return eintraege
	}, [filters, search, projects, assignees])

	// Ein einzelnes Schild wegnehmen. Die Suche ist kein Feld der Filter,
	// deshalb der eine Sonderfall.
	const entferneFilter = eintrag => {
		if (eintrag.feld === 'search') return setSearch('')

		setFilters(prev => ({
			...prev,
			[eintrag.feld]: prev[eintrag.feld].filter(wert => wert !== eintrag.wert)
		}))
	}

	const toggleSelect = id => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

	const allSelected = rows?.length > 0 && rows.every(row => selected.includes(row.id))
	const toggleAll = () => setSelected(allSelected ? [] : rows.map(row => row.id))

	// Zum Zuweisen zählt null als "ohne Projekt" — die 0 ist nur die
	// Filter-Konvention der Liste, im Feld selbst steht null.
	const bulkProjectOptions = useMemo(
		() => [
			{ value: null, label: 'Ohne Projekt' },
			...projects.map(project => ({ value: project.id, label: project.title }))
		],
		[projects]
	)

	/*
	 * Eine Stelle für beide Sammelaktionen. `tat` beschreibt in der Meldung,
	 * was passiert ist — "auf „Zur Abnahme" gesetzt", "dem Projekt X zugeordnet".
	 */
	const bulkPatch = (felder, tat) => {
		const anzahl = selected.length
		// `disableToast`: Der eigene Satz benennt den Vorgang, der schiefging.
		http.put(TApi.ADMIN_TICKET_BULK, { ids: selected, ...felder }, { disableToast: true })
			.then(() => {
				toast.success(`${anzahl} ${anzahl === 1 ? 'Anforderung' : 'Anforderungen'} ${tat}.`)
				setSelected([])
				afterChange()
			})
			.catch(error => notifyError(error, 'Die Änderung konnte nicht gespeichert werden.'))
	}

	const afterChange = () => {
		load()
		loadProjects()
		// Ein Statuswechsel verschiebt die Zahlen an den Reitern - sonst stünde
		// dort noch die Menge von vor der Änderung.
		loadCounts()
		// Und ebenso die Auswertung der Anfragen je Bundesland.
		setStatsKey(vorher => vorher + 1)
	}

	const openDetail = id => {
		setDetailSiblings(rows.map(row => row.id))
		setOpenId(id)
	}

	/*
	 * Direkt zu einer Anforderung: `?nr=123` (etwa aus der Erinnerung „Offene
	 * Prüfungen") öffnet sie gleich im Fenster — unabhängig von Sicht, Filter
	 * und Seite, deshalb wird sie gezielt geholt und nicht in `rows` gesucht.
	 * Gibt es sie nicht oder darf die Rolle sie nicht sehen, genügt ein kurzer
	 * Hinweis; die Liste bleibt stehen.
	 *
	 * Beim Schließen verschwindet `nr` wieder aus der Adresse — sonst ginge
	 * die Anforderung bei jedem Neuladen erneut auf.
	 */
	const entferneNummer = () => {
		const params = new URLSearchParams(location.search)
		if (!params.has('nr')) return
		params.delete('nr')
		const rest = params.toString()
		history.replace({ pathname: location.pathname, search: rest ? `?${rest}` : '' })
	}

	useEffect(() => {
		const roh = new URLSearchParams(location.search).get('nr')
		if (roh === null) return undefined
		const nummer = Number(roh)
		let aktiv = true
		const hinweis = () =>
			toast.info(`Die Anforderung #${roh} gibt es nicht, oder sie ist für Sie nicht sichtbar.`, {
				toastId: `nr-${roh}`
			})
		if (!Number.isInteger(nummer) || nummer <= 0) {
			hinweis()
			entferneNummer()
			return undefined
		}
		http.get(TApi.ADMIN_TICKET_ID(nummer), { disableToast: true })
			.then(() => {
				if (!aktiv) return
				setDetailSiblings([nummer])
				setOpenId(nummer)
			})
			.catch(error => {
				if (!aktiv) return
				const status = error?.response?.status
				if (status === 403 || status === 404) hinweis()
				else notifyError(error, 'Die Anforderung konnte nicht geladen werden.')
				entferneNummer()
			})
		return () => {
			aktiv = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search])

	const schliesseDetail = () => {
		setOpenId(null)
		entferneNummer()
	}

	if (rows === null) return <Loading />

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title="Anforderungen & Anfragen" bg="#f8faff">
				<div className={styles.headerActions}>
					<Btn theme="secondary" size="small" onClick={() => setShowProjects(true)}>
						Projekte verwalten
					</Btn>
					<Btn theme="primary" size="small" onClick={() => setEditing({})}>
						Neue Anforderung
					</Btn>
				</div>
			</MiniHeader>

			{/*
			 * Bleibt beim Filtern stehen: Die Reiter beantworten die Frage vor der
			 * Filterleiste — geht mich das etwas an? — und nicht die danach.
			 */}
			<div className={styles.scopeTabs} role="tablist" ref={scopeRef}>
				{sichten.map(entry => {
					const offen = scopeCounts?.[entry.key]
					/*
					 * „An mein Bundesland" ist für eine Landesadministration der
					 * Reiter, der sie angeht — unter fünf gleich aussehenden war
					 * er nicht als solcher zu erkennen. Er traegt deshalb den
					 * orangen Akzent, und seine offene Zahl leuchtet beim
					 * Anzeigen kurz auf (nur, wenn es etwas Offenes gibt).
					 */
					const eigen = entry.key === 'myState'

					return (
						<button
							key={entry.key}
							type="button"
							role="tab"
							aria-selected={scope === entry.key}
							className={clsx(
								styles.scopeTab,
								scope === entry.key && styles.scopeTabActive,
								eigen && styles.scopeTabEigen
							)}
							title={eigen ? `Ihr Bundesland: ${stateFullLabel(eigenesLand)}` : undefined}
							onClick={() => setzeScope(entry.key)}
						>
							{entry.label}
							{offen !== undefined && (
								<span
									className={clsx(
										styles.scopeCount,
										eigen && styles.scopeCountEigen,
										eigen && landPuls.leuchtet && PULS_KLASSE
									)}
									onAnimationEnd={eigen ? landPuls.onAnimationEnd : undefined}
									title="Offen: weder abgeschlossen noch verworfen"
								>
									{offen}
								</span>
							)}
						</button>
					)
				})}
			</div>

			{/*
			 * Ring statt Funnel: Der Funnel zeigt den Ablauf des ganzen Bestands.
			 * Bei dem, was jemandem zugewiesen ist, ist nicht der Weg die Frage,
			 * sondern die Verteilung — wie viel liegt an, wie viel läuft.
			 *
			 * Der Trichter bleibt immer sichtbar: Er ist Übersicht **und**
			 * Hauptfilter in einem und beantwortet die erste Frage an diese
			 * Seite — wo steht der Bestand gerade?
			 */}
			{inZuweisungsSicht ? (
				<StatusDonut
					counts={statusCounts}
					value={filters.status}
					onChange={next => setFilters(prev => ({ ...prev, status: next }))}
				/>
			) : (
				<StatusFunnel
					counts={statusCounts}
					value={filters.status}
					options={statusOptions}
					hints={statusHints}
					label="Statusfilter"
					onChange={next => setFilters(prev => ({ ...prev, status: next }))}
				/>
			)}

			{/*
			 * Alles Weitere, was zugleich auswertet und filtert, in **einem**
			 * zuklappbaren Block: Verteilung nach Personen, Herkunft, Anfragen
			 * je Bundesland. Einzeln standen sie als drei lose Kästen zwischen
			 * Trichter und Liste — man scrollte an ihnen vorbei, statt sie zu
			 * benutzen. Was sie eingestellt haben, steht ohnehin in der Leiste
			 * „Aktive Filter"; zugeklappt geht also nichts verloren.
			 */}
			<section className={styles.auswertung}>
				<button
					type="button"
					className={styles.auswertungKopf}
					aria-expanded={auswertungOffen}
					onClick={klappeAuswertung}
				>
					<svg
						className={clsx(styles.auswertungPfeil, auswertungOffen && styles.auswertungPfeilOffen)}
						viewBox="0 0 10 6"
						aria-hidden="true"
					>
						<path
							d="M1 1l4 4 4-4"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					<span className={styles.auswertungTitel}>Auswertung</span>
					<span className={styles.auswertungHinweis}>
						Verteilung, Herkunft und Anfragen je Bundesland — jeder Klick darin filtert die Liste
					</span>
				</button>

				{auswertungOffen && (
					<div className={styles.auswertungInhalt}>
						{/*
						 * Aufgeklappt und leer ist der schlechteste Zustand: Der
						 * Kopf verspricht drei Auswertungen, darunter steht nichts
						 * — man klappt zu, klappt auf und sucht den Fehler bei
						 * sich. Ein Satz beantwortet die Frage, die man sonst
						 * stellt.
						 */}
						{auswertungLeer && (
							<p className={styles.auswertungLeer}>
								Noch keine Anfragen über den Hilfe-Knopf. Sobald Schüler:innen, Lehrkräfte oder
								Unterstützer:innen etwas melden, stehen hier die Herkunft und die Auswertung je
								Bundesland. Die Verteilung nach Status zeigt der Trichter darüber.
							</p>
						)}

						{/*
						 * Wo die Arbeit liegt. Nur dort, wo die Frage eine Antwort
						 * hat: In „Mir zugewiesen" bin ich es immer selbst.
						 */}
						{inZuweisungsSicht && scope !== 'assignedToMe' && (
							<AssigneeBars
								counts={assigneeCounts}
								assignees={assignees}
								value={filters.assignedTo}
								onChange={next => setFilters(prev => ({ ...prev, assignedTo: next }))}
							/>
						)}

						{/*
						 * Woher der Bestand kommt. Steht vor der Auswertung je
						 * Bundesland: erst die Frage, ob überhaupt etwas von außen
						 * kam, dann die, wie es damit weiterging.
						 */}
						<OriginBar
							counts={originCounts}
							gesamt={enduserTotal}
							value={filters.herkunft}
							onChange={next => setFilters(prev => ({ ...prev, herkunft: next }))}
						/>

						{/* Zeigt sich nur, wo es Anfragen gibt. */}
						{(scope === 'all' || scope === 'myState') && (
							<RequestStats
								activeState={filters.state}
								reloadKey={statsKey}
								eigenesLand={eigenesLand}
								/*
								 * Kurzwahl auf dasselbe Feld wie die Filterleiste —
								 * kein zweiter Zustand daneben. Ein zweiter Klick
								 * hebt die Einschränkung wieder auf.
								 */
								onPick={wert =>
									setFilters(prev => ({
										...prev,
										state: prev.state.includes(wert)
											? prev.state.filter(x => x !== wert)
											: [...prev.state, wert]
									}))
								}
							/>
						)}
					</div>
				)}
			</section>

			<ListToolbar
				className={styles.toolbar}
				search={search}
				onSearchChange={setSearch}
				searchPlaceholder="Nach Nummer, Titel oder Beschreibung suchen"
				filters={filterConfig}
				filterValues={filters}
				onFilterChange={setFilters}
				sort={sort}
				sortOptions={sortOptions}
				onSortChange={setSort}
				view={view}
				onViewChange={setView}
				onReset={reset}
				/*
				 * Zurückgesetzt wird in der Leiste „Aktive Filter" darunter —
				 * an **einer** Stelle, zusammen mit der Aufzählung dessen, was
				 * zurückgenommen wird. Zwei Knöpfe mit demselben Zweck an zwei
				 * Stellen waren Teil des Problems.
				 */
				showReset={false}
				count={count}
				countLabel={count === 1 ? 'Anforderung' : 'Anforderungen'}
				collapsibleFilters
				extraActive={
					filters.status.length +
					filters.projectId.length +
					filters.assignedTo.length +
					filters.herkunft.length
				}
			>
				<div className={styles.projectFilter}>
					<span className={styles.projectFilterLabel}>Projekt</span>
					<Select
						classNamePrefix="select"
						className={styles.projectSelect}
						options={projectOptions}
						value={selectedProjects}
						onChange={options =>
							setFilters(prev => ({
								...prev,
								projectId: (options || []).map(option => option.value)
							}))
						}
						isMulti
						isClearable
						isSearchable
						closeMenuOnSelect={false}
						placeholder="Projekte suchen …"
						noOptionsMessage={() => 'Kein Projekt gefunden'}
						aria-label="Nach Projekten filtern"
					/>
				</div>
			</ListToolbar>

			{/*
			 * Was gerade einschränkt — aus allen Quellen zusammengetragen,
			 * direkt über der Liste und beim Scrollen mitlaufend.
			 */}
			<ActiveFilters eintraege={aktiveFilter} onEntfernen={entferneFilter} onAllesZuruecksetzen={reset} />

			{selected.length > 0 && (
				<div className={styles.bulkBar}>
					<span className={styles.bulkCount}>{selected.length} ausgewählt</span>
					<Select
						classNamePrefix="select"
						className={styles.bulkSelect}
						options={bulkProjectOptions}
						value={null}
						onChange={option =>
							bulkPatch(
								{ projectId: option.value },
								option.value === null
									? 'aus ihrem Projekt gelöst'
									: `dem Projekt „${option.label}" zugeordnet`
							)
						}
						isSearchable
						placeholder="Projekt zuweisen …"
						noOptionsMessage={() => 'Kein Projekt gefunden'}
						aria-label="Ausgewählten Anforderungen ein Projekt zuweisen"
					/>
					<Select
						classNamePrefix="select"
						className={styles.bulkSelect}
						options={statusOptions}
						value={null}
						onChange={option => bulkPatch({ status: option.value }, `auf „${option.label}" gesetzt`)}
						placeholder="Status setzen …"
						aria-label="Status der ausgewählten Anforderungen setzen"
					/>
					<Btn theme="secondary" size="small" onClick={() => setSelected([])}>
						Auswahl aufheben
					</Btn>
				</div>
			)}

			{!rows.length ? (
				<div className={styles.empty}>
					{search || Object.values(filters).some(v => v.length)
						? 'Keine Anforderung passt zu Suche und Filtern.'
						: leerText(scope)}
				</div>
			) : view === 'list' ? (
				<div className={styles.tableWrap}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.colCheck}>
									<input
										type="checkbox"
										checked={allSelected}
										onChange={toggleAll}
										aria-label="Alle Anforderungen auswählen"
									/>
								</th>
								{COLUMNS.map(column => {
									const active = column.sortField === sortField
									const sortable = Boolean(column.sortField)

									return (
										<th
											key={column.key}
											className={clsx(
												column.className && styles[column.className],
												sortable && styles.sortable,
												active && styles.sortActive
											)}
											// Beim Symbolkopf trägt der Tooltip den Spaltennamen.
											title={column.title || (column.icon ? column.label : undefined)}
											aria-label={column.icon ? column.label : undefined}
											aria-sort={
												active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
											}
											onClick={sortable ? () => sortBy(column) : undefined}
										>
											{column.icon ? <SpaltenSymbol name={column.icon} /> : column.label}
											{sortable && (
												<span className={styles.sortArrow} aria-hidden="true">
													{active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
												</span>
											)}
										</th>
									)
								})}
							</tr>
						</thead>
						<tbody>
							{rows.map(ticket => (
								<tr key={ticket.id} className={styles.row} onClick={() => openDetail(ticket.id)}>
									{/* Die Zeile öffnet das Detail — das Kästchen nicht. */}
									<td className={styles.colCheck} onClick={event => event.stopPropagation()}>
										<input
											type="checkbox"
											checked={selected.includes(ticket.id)}
											onChange={() => toggleSelect(ticket.id)}
											aria-label={`Anforderung #${ticket.id} auswählen`}
										/>
									</td>
									<td className={styles.colId}>#{ticket.id}</td>
									<td className={styles.titleCell}>{ticket.title}</td>
									<td>
										<span className={styles[`type${ticket.type}`]}>
											{ticketTypeLabel(ticket.type)}
										</span>
									</td>
									<td>
										<span className={`${styles.badge} ${styles[ticket.status]}`}>
											{ticketStatusLabel(ticket.status)}
										</span>
									</td>
									<td className={styles.colScore}>
										<span className={scoreClass(ticket.score)}>{ticket.score}</span>
									</td>
									{/*
									 * Das Kürzel statt des Titels: „Wartung und
									 * Kleinentwicklung 2025/26" brauchte hier drei
									 * Zeilen und schob die übrigen Spalten aus dem
									 * Bild. Der volle Name steht im Tooltip — und im
									 * Fenster der Anforderung ohnehin.
									 *
									 * Ein Projekt ohne Kürzel (angelegt, bevor es das
									 * Feld gab) fällt auf den Titel zurück: lieber
									 * breit als leer.
									 */}
									<td className={styles.colProject}>
										{ticket.project ? (
											<span className={styles.projectTag} title={ticket.project.title}>
												{ticket.project.shortTitle || ticket.project.title}
											</span>
										) : (
											<span className={styles.muted}>—</span>
										)}
									</td>
									<td className={styles.colShots}>{ticket.screenshotCount || '—'}</td>
									<td className={styles.colShots}>{ticket.commentCount || '—'}</td>
									<td className={styles.muted}>
										<Zeitstempel wert={ticket.updatedAt} />
									</td>
									{/*
									 * Wer die Anforderung in die Welt gesetzt hat und
									 * wann. Bei einer Meldung über den Hilfe-Knopf ist
									 * das die meldende Person und der Zeitpunkt der
									 * Meldung — dieselbe Frage, andere Herkunft.
									 */}
									<td className={styles.muted}>
										{(() => {
											const wer = herkunft(ticket)
											const inhalt = !wer.extern ? (
												wer.name === '—' ? null : (
													wer.name
												)
											) : (
												<span
													className={styles.extern}
													title={`Über den Hilfe-Knopf gemeldet · ${wer.land}`}
												>
													<svg viewBox="0 0 16 16" aria-hidden="true">
														<circle
															cx="8"
															cy="5"
															r="2.6"
															fill="none"
															stroke="currentColor"
															strokeWidth="1.5"
														/>
														<path
															d="M2.8 14c0-2.8 2.3-4.6 5.2-4.6s5.2 1.8 5.2 4.6"
															fill="none"
															stroke="currentColor"
															strokeWidth="1.5"
															strokeLinecap="round"
														/>
													</svg>
													{wer.name}
												</span>
											)

											return (
												<NameMitDatum
													name={inhalt}
													rolle={wer.rolle}
													datum={ticket.createdAt}
												/>
											)
										})()}
									</td>
									{/*
									 * Name und Zeitpunkt der Zuweisung. `assignedAt`
									 * pflegt der Server (Modell Ticket): Er folgt der
									 * Zuweisung und nicht jeder Änderung.
									 */}
									<td className={styles.muted}>
										<NameMitDatum
											name={ticket.assignedTo?.name}
											rolle={ticket.assignedTo?.role}
											datum={ticket.assignedAt}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : (
				<div className={styles.tiles}>
					{rows.map(ticket => {
						const isOpen = expanded[ticket.id]
						const text = ticket.description || ''
						// Auf der Kachel steht nur der Anfang. Der Rest kommt per
						// "Mehr lesen" - sonst werden die Kacheln unterschiedlich
						// hoch und das Raster zerfaellt.
						const isLong = text.length > KACHEL_ZEICHEN

						return (
							<article key={ticket.id} className={styles.tile}>
								<header className={styles.tileHead}>
									<span className={styles.tileHeadLeft}>
										<input
											type="checkbox"
											checked={selected.includes(ticket.id)}
											onChange={() => toggleSelect(ticket.id)}
											aria-label={`Anforderung #${ticket.id} auswählen`}
										/>
										<span className={styles.tileId}>#{ticket.id}</span>
									</span>
									<div className={styles.tileHeadRight}>
										<span className={scoreClass(ticket.score)} title="Score">
											{ticket.score}
										</span>
										<span className={`${styles.badge} ${styles[ticket.status]}`}>
											{ticketStatusLabel(ticket.status)}
										</span>
									</div>
								</header>

								<h3 className={styles.tileTitle}>{ticket.title}</h3>

								{text && (
									<p className={styles.tileText}>
										{isOpen || !isLong ? text : `${text.slice(0, KACHEL_ZEICHEN)}…`}
									</p>
								)}

								{isLong && (
									<button
										type="button"
										className={styles.moreLink}
										onClick={() => setExpanded(prev => ({ ...prev, [ticket.id]: !isOpen }))}
									>
										{isOpen ? 'Weniger' : 'Mehr lesen'}
									</button>
								)}

								<footer className={styles.tileFoot}>
									<span className={styles[`type${ticket.type}`]}>{ticketTypeLabel(ticket.type)}</span>
									{ticket.project && <span className={styles.muted}>{ticket.project.title}</span>}
									{ticket.assignedTo?.name && (
										<span className={styles.muted}>
											an {ticket.assignedTo.name}
											<RoleChip role={ticket.assignedTo.role} className={styles.kachelRolle} />
										</span>
									)}
									{(() => {
										const wer = herkunft(ticket)
										if (!wer.extern) {
											return wer.name !== '—' ? (
												<span className={styles.muted}>
													{wer.name}
													<RoleChip role={wer.rolle} className={styles.kachelRolle} />
												</span>
											) : null
										}

										return (
											<span
												className={styles.extern}
												title={`Über den Hilfe-Knopf gemeldet · ${wer.land}`}
											>
												{wer.name}
												<RoleChip role={wer.rolle} />
											</span>
										)
									})()}
									{Boolean(ticket.screenshotCount) && (
										<span className={styles.muted}>
											{ticket.screenshotCount} Anhang
											{ticket.screenshotCount > 1 ? 'e' : ''}
										</span>
									)}
									{Boolean(ticket.commentCount) && (
										<span className={styles.muted}>
											{ticket.commentCount} Beitrag
											{ticket.commentCount > 1 ? 'e' : ''}
										</span>
									)}
									<Btn size="small" theme="secondary" onClick={() => openDetail(ticket.id)}>
										Öffnen
									</Btn>
								</footer>
							</article>
						)
					})}
				</div>
			)}

			{openId !== null && (
				<TicketDetail
					ticketId={openId}
					// Blaettern zwischen Anforderungen in der Reihenfolge der Liste
					// zum Zeitpunkt des Oeffnens (siehe detailSiblings).
					siblings={detailSiblings}
					projects={projects}
					assignees={assignees}
					onNavigate={setOpenId}
					onClose={schliesseDetail}
					onChanged={afterChange}
					onEdit={ticket => {
						schliesseDetail()
						setEditing(ticket)
					}}
				/>
			)}

			{editing !== null && (
				<TicketFormModal
					ticket={editing}
					projects={projects}
					assignees={assignees}
					onClose={() => setEditing(null)}
					onSaved={() => {
						setEditing(null)
						afterChange()
					}}
				/>
			)}

			{showProjects && (
				<ProjectsModal projects={projects} onClose={() => setShowProjects(false)} onChanged={afterChange} />
			)}
		</div>
	)
}
